/**
 * Ordering for dotted numeric app versions ("1.4.0", "26.9.1", "0.0.317").
 *
 * Written rather than pulled in: the app versions this compares are produced
 * by our own build workflows and are always dotted integers, so full semver
 * (pre-release tags, build metadata, range syntax) would be a dependency
 * carrying rules we never exercise.
 *
 * The case that makes a naive string compare wrong, and the reason this
 * exists at all: '1.10.0' < '1.9.0' lexically, but 1.10.0 is the newer build.
 * A forced-update gate using string comparison would lock out exactly the
 * players who had already updated.
 */

/**
 * Segments, strictly. A segment must be all digits — '1.4.0-beta' is NOT
 * salvaged as [1,4,0], it is rejected outright. Half-reading a version we do
 * not recognise is worse than admitting we cannot order it, because the
 * caller's fallback is to let the player in.
 */
const segments = (version: string): number[] =>
  version
    .trim()
    .split('.')
    .map((part) => {
      if (!/^\d+$/.test(part)) return NaN;
      const value = Number(part);
      // Above MAX_SAFE_INTEGER two different digit strings can land on the
      // same double, which would silently report a stale version as equal to
      // the floor and let it through. Treat that as unorderable instead; the
      // callers all fail open, which is the right direction.
      return Number.isSafeInteger(value) ? value : NaN;
    });

/**
 * Whether `version` is a shape this comparator can order: at least one
 * segment, every segment numeric. Callers use this to decide whether a
 * comparison is meaningful at all rather than acting on a garbage ordering.
 */
export const isComparableVersion = (version: string | null | undefined): version is string => {
  if (!version || typeof version !== 'string') return false;
  const parts = segments(version);
  return parts.length > 0 && parts.every((part) => Number.isFinite(part));
};

/**
 * -1 if a < b, 0 if equal, 1 if a > b. Missing trailing segments count as 0,
 * so '1.4' and '1.4.0' are equal.
 *
 * Both arguments must satisfy isComparableVersion; anything else is a caller
 * error and returns 0 (treated as equal) so a gate built on this fails open
 * rather than blocking on a version it could not read.
 */
export const compareVersions = (a: string, b: string): number => {
  if (!isComparableVersion(a) || !isComparableVersion(b)) return 0;

  const left = segments(a);
  const right = segments(b);
  const length = Math.max(left.length, right.length);

  for (let i = 0; i < length; i += 1) {
    const l = left[i] ?? 0;
    const r = right[i] ?? 0;
    if (l !== r) return l < r ? -1 : 1;
  }
  return 0;
};

/** True when `version` is strictly older than `floor`. */
export const isOlderThan = (version: string, floor: string): boolean =>
  compareVersions(version, floor) < 0;
